import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useFetchTradeSettings,
  useSessionsStore,
  useSettingsStore,
} from 'common/stores';
import { TextInput } from 'core/components/form';
import { CircularIndicator } from 'core/components/uikit';
import { DisclosureWithTitle } from 'features/components';

import {
  TradeSettingsFormData,
  tradeSettingsFormSchema,
} from './validationSchema';

const TradeSettingsForm = () => {
  const { t } = useTranslation();
  const { data: tradeSettings, isLoading } = useFetchTradeSettings();
  const isAnySessionRunning = useSessionsStore((state) =>
    state.sessions?.some((session) => session?.status === 'active')
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<TradeSettingsFormData>({
    resolver: yupResolver(tradeSettingsFormSchema),
    mode: 'all',
    values: tradeSettings
      ? {
          maxOpenPositionsPerDay: tradeSettings.maxOpenPositionsPerDay ?? 50,
          maxOpenPositions: tradeSettings.maxOpenPositions ?? 75,
          sessionTradeLimit: tradeSettings.sessionTradeLimit ?? 3,
        }
      : undefined,
  });

  const isButtonDisabled =
    !isValid || isSubmitting || isLoading || isAnySessionRunning;

  const onSubmit = handleSubmit(async (values) => {
    const { updateTradeSettings } = useSettingsStore.getState();

    const success = await updateTradeSettings(values);

    if (success) {
      enqueueSnackbar(t('pages.settings.tradeSettingsForm.success'), {
        variant: 'success',
        hideIconVariant: true,
      });
    }

    if (!success && tradeSettings) {
      reset({
        maxOpenPositionsPerDay: tradeSettings.maxOpenPositionsPerDay ?? 50,
        maxOpenPositions: tradeSettings.maxOpenPositions ?? 75,
        sessionTradeLimit: tradeSettings.sessionTradeLimit ?? 3,
      });
    }
  });

  return (
    <DisclosureWithTitle
      title={<h4>{t('pages.settings.tradeSettingsForm.title')}</h4>}
      defaultOpen
    >
      <form
        className="flex max-w-full flex-col gap-4 sm:max-w-80"
        onSubmit={onSubmit}
      >
        <TextInput
          control={control}
          name="maxOpenPositionsPerDay"
          componentProps={{
            type: 'number',
            label: t(
              'pages.settings.tradeSettingsForm.fields.maxOpenPositionsPerDay.label'
            ),
          }}
        />
        <TextInput
          control={control}
          name="maxOpenPositions"
          componentProps={{
            type: 'number',
            label: t(
              'pages.settings.tradeSettingsForm.fields.maxOpenPositions.label'
            ),
          }}
        />
        <TextInput
          control={control}
          name="sessionTradeLimit"
          componentProps={{
            type: 'number',
            label: t(
              'pages.settings.tradeSettingsForm.fields.sessionTradeLimit.label'
            ),
          }}
        />
        <button type="submit" disabled={isButtonDisabled}>
          {isSubmitting || isLoading ? <CircularIndicator /> : t('common.save')}
        </button>
      </form>
    </DisclosureWithTitle>
  );
};

export default TradeSettingsForm;
